import { Component, OnDestroy, OnInit } from '@angular/core';
import { RootState } from '@next/core-lib/app';
import { FilterAction } from '@next/core-lib/components';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { FormatLocationPipe } from '@next/core-lib/pipes';
import { SettingsService, SettingsStorage } from '@next/core-lib/settings';
import { UserSelectors } from '@next/core-lib/user';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, Subject } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';

import {
  DeliveryCategories,
  DeliveryColumnTypes,
} from '../../../delivery-data/delivery-data.model';
import { DeliveryDataService } from '../../../delivery-data/services/delivery-data.service';
import {
  clearDeliveryTotalsRequest,
  printDeliveryReportRequest,
  loadDeliveryTotalsRequest,
} from '../../../delivery-data/store/delivery-data.actions';
import { selectDeliveryTotals } from '../../../delivery-data/store/delivery-data.selectors';
import { HierarchicalData } from '../../../shared/hierarchical-data/hierarchical-data.model';
import { settingsContextName, SettingsKeys } from '../../../shared/models/shared.models';
import { PageHierarchicalDataFilterComponent } from '../../../shared/page-hierarchical-data-filter/page-hierarchical-data-filter.component';
import { FormatBrsNumberPipe } from '../../../shared/pipes/format-brs-number.pipe';
import { selectCustomerBrsNumbers, selectCustomerKvkNumbers } from '../../../store/selectors';

@Component({
  selector: 'app-bex-totals',
  templateUrl: './bex-totals.component.html',
  providers: [FormatBrsNumberPipe, FormatLocationPipe],
  imports: [PageHierarchicalDataFilterComponent, TranslatePipe],
})
export class BexTotalsComponent implements OnInit, OnDestroy {
  public readonly totalsTableConfig = {
    prefix: 'bex.bex-totals.',
    localeService: this.localeService,
    columns: [
      DeliveryColumnTypes.description,
      DeliveryColumnTypes.productTitle,
      DeliveryColumnTypes.unitQuantity,
      DeliveryColumnTypes.vemAverage,
      DeliveryColumnTypes.roughProteinAverage,
      DeliveryColumnTypes.nitrogenAverage,
      DeliveryColumnTypes.phosphorusAverage,
      DeliveryColumnTypes.dryMatterAmountAverage,
      DeliveryColumnTypes.roughAshesAverage,
    ],
    digitsInfo: {
      [DeliveryColumnTypes.unitQuantity]: '1.0-0',
      [DeliveryColumnTypes.vemAverage]: '1.0-0',
      [DeliveryColumnTypes.roughProteinAverage]: '1.0-2',
      [DeliveryColumnTypes.nitrogenAverage]: '1.0-2',
      [DeliveryColumnTypes.phosphorusAverage]: '1.0-2',
      [DeliveryColumnTypes.dryMatterAmountAverage]: '1.0-2',
      [DeliveryColumnTypes.roughAshesAverage]: '1.0-2',
    },
    hideUnits: true,
  };

  public readonly filterActions: FilterAction[] = [
    {
      icon: 'file_download',
      viewValue: 'Excel',
      value: 'xlsx',
    },
    {
      icon: 'file_download',
      viewValue: 'PDF',
      value: 'pdf',
    },
    {
      icon: 'file_download',
      viewValue: 'CSV',
      value: 'csv',
    },
  ];

  public readonly headings = this.deliveryDataService.getHeadings(
    this.totalsTableConfig.columns,
    'bex.bex-totals.',
  );

  public readonly exportFilename = `${this.localeService.translate('bex.bex-totals.fileName')}`;

  public data$: Observable<HierarchicalData[]>;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly store: Store<RootState>,
    private readonly localeService: LocaleService,
    private readonly settingsService: SettingsService,
    private readonly deliveryDataService: DeliveryDataService,
    private readonly formatBrsNumberPipe: FormatBrsNumberPipe,
  ) {}

  ngOnInit(): void {
    this.data$ = this.store
      .select(selectDeliveryTotals(this.totalsTableConfig))
      .pipe(takeUntil(this.destroy$));
  }

  ngOnDestroy(): void {
    this.store.dispatch(clearDeliveryTotalsRequest());

    this.destroy$.next();
    this.destroy$.complete();
  }

  public loadData(): void {
    this.store.dispatch(loadDeliveryTotalsRequest({ category: DeliveryCategories.bex }));
  }

  public printReport = () =>
    combineLatest([
      this.settingsService.getSettings({
        contextName: settingsContextName,
        storage: SettingsStorage.PlatformSession,
        key: SettingsKeys.dateRange,
      }),
      this.settingsService.getSettings({
        contextName: settingsContextName,
        storage: SettingsStorage.PlatformSession,
        key: SettingsKeys.brsNumber,
      }),
      this.store.select(selectCustomerBrsNumbers),
      this.store.select(selectCustomerKvkNumbers),
      this.store.select(UserSelectors.selectCustomer),
      this.data$,
    ])
      .pipe(
        take(1),
        map(
          ([
            dateRange,
            filterBrsNumberResult,
            customerBrsNumbers,
            customerKvkNumbers,
            customer,
            data,
          ]) => {
            const startDate = new Date((dateRange?.[0].data as { startDate: string })?.startDate);
            const endDate = new Date((dateRange?.[0].data as { endDate: string })?.endDate);
            const filterBrsNumber = filterBrsNumberResult[0].data as string;
            const brsNumberToDisplay = customerBrsNumbers.find(
              (customerBrsNumber) => customerBrsNumber.brsNumber === filterBrsNumber,
            );
            const kvkNumberToDisplay =
              customerKvkNumbers.find((kvkNumber) =>
                brsNumberToDisplay.relatedLocations.some(
                  (brsLoc) => brsLoc.id === kvkNumber.relatedLocation.id,
                ),
              ) ?? customerKvkNumbers[0];

            this.store.dispatch(
              printDeliveryReportRequest({
                report: {
                  title: this.localeService.translate('bex.title-totals'),
                  subTitle: `${this.localeService.translate(
                    'bex.report.sub-title',
                  )} ${this.deliveryDataService.formatDateTime(new Date())}`,
                  fileName: this.deliveryDataService.formatExportFileName(
                    this.localeService.translate('bex.bex-totals.fileName'),
                    customer.externalId,
                    startDate,
                    endDate,
                  ),
                  legend: {
                    [this.localeService.translate('bex.report.legend.customer')]:
                      `${customer.name} (${customer.externalId})`,
                    ...(kvkNumberToDisplay
                      ? {
                          [this.localeService.translate('bex.report.legend.kvk-number')]:
                            kvkNumberToDisplay.kvkNumber,
                        }
                      : {}),
                    [this.localeService.translate('bex.report.legend.brs-number')]:
                      this.formatBrsNumberPipe.transform(
                        customerBrsNumbers.find(
                          (customerBrsNumber) => customerBrsNumber.brsNumber === filterBrsNumber,
                        ),
                      ),
                    [this.localeService.translate('bex.report.legend.start-date')]:
                      this.deliveryDataService.formatDateTime(startDate),
                    [this.localeService.translate('bex.report.legend.end-date')]:
                      this.deliveryDataService.formatDateTime(endDate),
                  },
                  tableHeadings:
                    this.totalsTableConfig?.columns?.map((column) =>
                      this.localeService.translate(`${this.totalsTableConfig.prefix}${column}`),
                    ) ?? [],
                  tableFooter: [
                    this.localeService.translate('bex.disclaimer1'),
                    this.localeService.translate('bex.disclaimer2'),
                    this.localeService.translate('bex.disclaimer3'),
                    this.localeService.translate('bex.disclaimer4'),
                  ],

                  contactInformation: {
                    email: this.localeService.translate('bex.report.contact.email'),
                    phoneNumber: this.localeService.translate('bex.report.contact.phone-number'),
                  },
                  rows: this.deliveryDataService.mapHierarchicalDataToPdfRows(data),
                },
              }),
            );
          },
        ),
      )
      .subscribe();
}
